import { all, any, pathOr } from 'ramda'
import { TargetingConditionType } from 'types/targeting'
import { tryParseFloat } from 'utils'
import { normalizeDriftUrl } from 'utils/url'

// The % of the page where the mouse needs to be for intent to be matched (ex. 0.5 = top 5% of the page)
const EXIT_INTENT_VIEWPORT_TRIGGER = 0.5

// The # of seconds required to be on the page before we can trigger exit intent - exists to keep it from auto-triggering if the mouse is in the wrong place on load
const EXIT_INTENT_SECONDS_ON_PAGE = 5

const resolveDeviceType = (userAgent: string) => {
  const userAgentLower = userAgent.toLowerCase()
  if (userAgentLower.match(/(mobi|phone|ipod|blackberry|docomo|iphone|android)/i)) {
    return 'mobile'
  }
  if (userAgentLower.match(/(ipad|kindle)/i)) {
    return 'tablet'
  } else {
    return 'desktop'
  }
}

const transformNumericConditionValues = (value: any) => {
  if (typeof value === 'string') {
    return tryParseFloat(value)
  }

  if (typeof value === 'object' && !!value.min && !!value.max) {
    return {
      min: tryParseFloat(value.min),
      max: tryParseFloat(value.max)
    }
  }

  return value
}

const getSecondsOnPage = (context: Record<string, any>) => {
  const drift_page_view_started = pathOr(0, ['_window', 'drift_page_view_started'])(context)
  const rightNow = Math.floor(new Date().getTime() / 1e3)
  return rightNow - drift_page_view_started
}

export const isAnyOf = (values: any, state: any) => {
  return any(val => state === val)(values)
}
export const notIsAnyOf = (values: any, state: any) => !isAnyOf(values, state)
export const is = (value: any, state: any) => value === state
export const notIs = (value: any, state: any) => value !== state
export const isMatch = (values: RegExp | RegExp[], state: string = '') => {
  if (!Array.isArray(values)) {
    values = [values]
  }

  return any<RegExp>((val) => val.test(state))(values)
}
export const notIsMatch = (values: any, state: any = '') => !isMatch(values, state)
export const lessThan = (value: any, state: any) => state < value
export const greaterThan = (value: any, state: any) => state > value
export const isBetween = (value: any, state: any) => (!!value.min && !!value.max) && (state >= value.min && state <= value.max)
export const includesAnyOf = (values: any, state: any = '') =>  any((val) => state.indexOf(val) !== -1)(values)
export const notIncludesAnyOf = (values: any, state: any = '') =>  all((val) => state.indexOf(val) === -1)(values)

export const startsWith = (values: any, state: any = '') =>  any((val) => state.indexOf(val) === 0)(values)

export const intersectionEmpty = (values: any, state: any = '') => any((val) => state.indexOf(val) === -1)(values)
export const notIntersectionEmpty = (values: any, state: any = '') => any((val) => state.indexOf(val) !== -1)(values)

export const isAvailable = (value: any, state: any = '') =>  !!value
export const isNotAvailable =  (value: any, state: any = '') => !value

export const CONDITION_TYPES : Record<string, TargetingConditionType> = {
  device: {
    async: false,
    selector: (context: Record<string, any>) => (resolveDeviceType(pathOr('', ['_window', 'userAgent'])(context))),
    operands: {
      isAnyOf,
      notIsAnyOf,
    },
  },
  siteVisits: {
    async: false,
    selector: (context: Record<string, any>) => pathOr(0, ['visitInformation', 'total'])(context),
    transformValueFn: (value: any): number | Record<string, number> => transformNumericConditionValues(value),
    operands: {
      is,
      notIs,
      lessThan,
      greaterThan,
      isBetween,
    },
  },
  daysSinceLastVisit: {
    async: false,
    selector: (context: Record<string, any>) => pathOr(0, ['visitInformation', 'daysSinceLastVisit'])(context),
    transformValueFn: (value: any): number | Record<string, number> => transformNumericConditionValues(value),
    operands: {
      is,
      notIs,
      lessThan,
      greaterThan,
      isBetween,
    },
  },
  referrer: {
    async: false,
    transformValueFn: (valueArray: string[]): string[] => (valueArray.map((url: string) => normalizeDriftUrl(url))),
    selector: (context: Record<string, any>) => normalizeDriftUrl(pathOr('', ['referrer'])(context)),
    operands: {
      is,
      notIs,
      isAnyOf,
      notIsAnyOf,
      includesAnyOf,
      notIncludesAnyOf,
    },
  },
  cookie: {
    async: false,
    selector: (context: Record<string, any>): string[] => (
      Object.entries(
        pathOr({}, ['cookies'])(context)
      ).map((entry: any) => entry.join("="))
    ),
    operands: {
      is: includesAnyOf,
      notIs: notIncludesAnyOf,
      isAnyOf: includesAnyOf,
      notIsAnyOf: notIncludesAnyOf,
      includesAnyOf,
      notIncludesAnyOf,
    },
  },
  scrollPct: {
    async: true,
    eventType: 'scroll',
    transformValueFn: (value: any): number | Record<string, number>  => transformNumericConditionValues(value),
    selector: (context: Record<string, any>) => {
     const scrollY = pathOr(0, ['_window', 'scrollY'])(context)
     const innerHeight = pathOr(0, ['_window', 'innerHeight'])(context)
     const scrollableHeight = pathOr(0, ['scrollableHeight'])(context)
     const scrollPosition = innerHeight + scrollY

     return (scrollPosition / scrollableHeight) * 100
    },
    operands: {
      lessThan,
      greaterThan,
      isBetween,
    },
  },
  secondsOnPage: {
    async: true,
    timer: true,
    eventType: 'timer',
    selector: (context: Record<string, any>) => getSecondsOnPage(context),
    transformValueFn: (value: string): number | Record<string, number>  => transformNumericConditionValues(value),
    operands: {
      lessThan,
      greaterThan,
      isBetween
    },
  },
  secondsOnSite: {
    async: true,
    timer: true,
    eventType: 'timer',
    transformValueFn: (value: string): number => transformNumericConditionValues(value),
    selector: (context: Record<string, any>) => {
      const drift_session_started = pathOr(0, ['_window', 'drift_session_started'])(context)
      const rightNow = Math.floor(new Date().getTime() / 1e3)

      return rightNow - drift_session_started
    },
    operands: {
      lessThan,
      greaterThan,
      isBetween
    },
  },
  exitIntent: {
    async: true,
    eventType: 'mouseleave',
    eventProperties: ['clientY'],
    selector: (context: Record<string, any> = {}) => {
      // When an async condition evaluates to true it evaluates through the full list a second time to generate an accurate audit log instead of stitching it together
      // That second runthrough of this condition looks at redux for an indication that intent has already calculated and matched
      const hasExitIntent = pathOr(false, ['hasExitIntent'])(context)

      if (hasExitIntent) {
        return true
      }

      const innerHeight = pathOr(0, ['_window', 'innerHeight'])(context)
      const clientY = pathOr(innerHeight, ['eventContext', 'clientY'])(context)
      const secondsOnPage = getSecondsOnPage(context)

      const result = (clientY / innerHeight) < EXIT_INTENT_VIEWPORT_TRIGGER && secondsOnPage > EXIT_INTENT_SECONDS_ON_PAGE
      return result
    },
    operands: {
      is,
      notIs
    },
  },
  outboundSession: {
    async: false,
    selector: (context: Record<string, any>) => pathOr(false, ['session', 'isOutbound'])(context),
    operands: {
      is,
      notIs,
    },
  },
  autoAssigneeId: {
    async: false,
    selector: (context: Record<string, any>) => pathOr('', ['embed', 'configuration', 'autoAssigneeId'])(context),
    operands: {
      is,
      notIs,
      isAnyOf,
      notIsAnyOf,
    },
  },
  inboxId: {
    async: false,
    selector: (context: Record<string, any>) => pathOr('', ['embed', 'configuration', 'inboxId'])(context),
    operands: {
      isAnyOf,
      notIsAnyOf,
    }
  },
}

export const isUnsupportedCondition = (condition: { field: string }) => {
  return !CONDITION_TYPES[condition.field]
}
